/* eslint-disable */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { PlayerProfileLink } from 'widgets/statspro/js/components/common/PlayerProfileLink';

/**
 * Renders an individual player item with their status/reason for ins and outs.
 *
 * Displays player name as a clickable link followed by an optional reason in parentheses.
 * Automatically adds commas between items except for the last item in the list.
 * Generates unique keys using player ID for React reconciliation.
 *
 * @param {object} props - The component props
 * @param {object} props.playerItem - The player item object containing player data and reason
 * @param {string|number} props.playerItem.playerId - Alternative player ID (fallback)
 * @param {string} props.playerItem.reason - Optional reason for the player's status (e.g., "injury", "suspension")
 * @param {object} props.playerItem.player - The nested player object
 * @param {string|number} props.playerItem.player.playerId - The player's unique identifier
 * @param {object} props.playerItem.player.playerName - The player's name object
 * @param {string} props.playerItem.player.playerName.givenName - The player's first name
 * @param {string} props.playerItem.player.playerName.surname - The player's surname
 * @param {number} props.index - The item's position in the list (used for comma placement and fallback keys)
 * @param {number} props.listLength - Total number of items in the list (determines if comma should be added)
 * @param {boolean} props.isAFLW - Whether this is an AFLW match (affects link URL format for external players)
 * @param {string} props.teamLineupsContext - Context for tracking ('match-centre' or other)
 * @param {string} props.prefix - Prefix for generating unique keys (typically team identifier)
 * @returns {React.JSX.Element} - A Fragment containing the player link and optional reason
 */
export const TAPlayerListItem = ({
    playerItem,
    index,
    listLength,
    isAFLW,
    teamLineupsContext,
    prefix
}) => {
    // Unique key fallback strategy using playerId or array index
    const playerId = playerItem?.playerId || playerItem?.player?.playerId;
    const uniqueKey = `TAFieldInsOuts__${prefix}__${playerId ?? index}`;

    const trackingInfo = {
        event: PULSE.app.common.CONSTANTS.TRACKING.EVENTS.SELECT.LINK,
        component_name:
            PULSE.app.common.CONSTANTS.TRACKING.VALUES.TEAM_LINEUPS.VIEW.FIELD,
        component_content_type:
            teamLineupsContext === 'match-centre'
                ? PULSE.app.common.CONSTANTS.TRACKING.CONTENT_TYPES.MATCH_CENTRE
                      .LINEUPS
                : PULSE.app.common.CONSTANTS.TRACKING.CONTENT_TYPES.LINEUPS
    };

    return (
        <Fragment key={uniqueKey}>
            <PlayerProfileLink
                classes="team-announcements__link"
                player={{
                    playerId: playerItem?.player?.playerId,
                    firstName: playerItem?.player?.playerName?.givenName,
                    surname: playerItem?.player?.playerName?.surname
                }}
                trackingInfo={trackingInfo}
                isAFLW={isAFLW}
            >
                <span>
                    {playerItem?.player?.playerName?.givenName}{' '}
                    {playerItem?.player?.playerName?.surname}
                </span>
            </PlayerProfileLink>
            <span>
                {playerItem?.reason ? ` (${playerItem?.reason})` : null}
                {index + 1 !== listLength ? `, ` : null}{' '}
            </span>
        </Fragment>
    );
};

TAPlayerListItem.propTypes = {
    playerItem: PropTypes.shape({
        playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        reason: PropTypes.string,
        player: PropTypes.shape({
            playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            playerName: PropTypes.shape({
                givenName: PropTypes.string,
                surname: PropTypes.string
            })
        })
    }),
    index: PropTypes.number.isRequired,
    listLength: PropTypes.number.isRequired,
    isAFLW: PropTypes.bool,
    teamLineupsContext: PropTypes.string,
    prefix: PropTypes.string.isRequired
};
